import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { feeAPI, employeesAPI } from '@/lib/api';
import { 
  CreditCard, DollarSign, Plus, Trash2, Calendar, User, 
  Loader2, AlertCircle, CheckCircle, Clock, Receipt, Percent,
  Banknote, Building2, Smartphone
} from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'Cash', label: 'Cash', icon: Banknote },
  { value: 'Bank Transfer', label: 'Bank Transfer', icon: Building2 },
  { value: 'JazzCash/Easypaisa', label: 'JazzCash/Easypaisa', icon: Smartphone },
];

export default function StudentFinanceTab({ student, user, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [feeSummary, setFeeSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash',
    received_by: '',
    notes: ''
  });
  
  const [newDiscount, setNewDiscount] = useState({
    discount_amount: '',
    reason: ''
  });

  useEffect(() => {
    if (student?.id) {
      fetchFeeSummary();
      fetchEmployees();
    }
  }, [student?.id]);

  const fetchFeeSummary = async () => {
    try {
      setLoading(true);
      const res = await feeAPI.getStudentFeeSummary(student.id);
      setFeeSummary(res.data);
    } catch (error) {
      console.error('Error fetching fee summary:', error);
      toast.error('Failed to load fee information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await employeesAPI.getAll();
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!newPayment.received_by) {
      toast.error('Please select who received the payment');
      return;
    }

    try {
      setSubmitting(true);
      const res = await feeAPI.addPayment(student.id, {
        ...newPayment,
        student_id: student.id,
        amount: parseFloat(newPayment.amount)
      });
      
      toast.success(res.data.message);
      
      if (res.data.saving_pot_credited) {
        toast.success(`💰 Rs. ${res.data.saving_pot_amount.toLocaleString()} credited to staff saving pot!`, {
          duration: 5000
        });
      }
      
      setShowAddPayment(false);
      setNewPayment({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Cash',
        received_by: '',
        notes: ''
      });
      fetchFeeSummary();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm('Are you sure you want to delete this payment? This will also remove any related saving pot credit.')) {
      return;
    }
    
    try {
      await feeAPI.deletePayment(student.id, paymentId);
      toast.success('Payment deleted');
      fetchFeeSummary();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const handleApplyDiscount = async () => {
    const amount = parseFloat(newDiscount.discount_amount);
    if (!newDiscount.discount_amount || amount <= 0) {
      toast.error('Please enter a valid discount amount');
      return;
    }
    if (!newDiscount.reason.trim()) {
      toast.error('Please provide a reason for the discount');
      return;
    }
    const totalFee = feeSummary?.total_fee || 0;
    const maxDiscount = Math.round(totalFee * 0.10);
    if (totalFee > 0 && amount > maxDiscount) {
      toast.error(`Discount cannot exceed 10% of the consultancy fee (max Rs. ${maxDiscount.toLocaleString()})`);
      return;
    }

    try {
      setSubmitting(true);
      await feeAPI.applyDiscount(student.id, {
        discount_amount: amount,
        reason: newDiscount.reason
      });
      toast.success('Discount applied successfully');
      setShowAddDiscount(false);
      setNewDiscount({ discount_amount: '', reason: '' });
      fetchFeeSummary();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!confirm('Are you sure you want to remove the discount?')) return;
    
    try {
      await feeAPI.removeDiscount(student.id);
      toast.success('Discount removed');
      fetchFeeSummary();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove discount');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Paid in Full</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"><Clock className="w-3 h-3 mr-1" /> Partial</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"><AlertCircle className="w-3 h-3 mr-1" /> Unpaid</Badge>;
    }
  };

  const getPaymentMethodIcon = (method) => {
    const found = PAYMENT_METHODS.find(m => m.value === method);
    return found ? found.icon : Banknote;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        <span className="ml-2 text-gray-500">Loading finance info...</span>
      </div>
    );
  }

  // Not a direct student - show message
  if (!feeSummary?.is_direct_student) {
    return (
      <div className="p-4 bg-gray-100 dark:bg-slate-800 rounded-lg text-center">
        <AlertCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-gray-600 dark:text-gray-300 font-medium">Fee Tracking Not Applicable</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          This is an agent-referred student (B2B). Counselling fee tracking is only available for direct students (B2C).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-finance-tab">
      {/* Fee Summary Card */}
      <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/30 dark:to-blue-900/30 rounded-xl border border-teal-200 dark:border-teal-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-teal-600" />
            Counselling / Service Fee
          </h3>
          {getStatusBadge(feeSummary.status)}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Fee</p>
            <p className="text-lg font-bold text-gray-800 dark:text-white">
              Rs. {feeSummary.total_fee?.toLocaleString()}
            </p>
            {feeSummary.destination && (
              <p className="text-xs text-teal-600 mt-1">{feeSummary.destination}</p>
            )}
          </div>
          
          {feeSummary.discount_amount > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Discount</p>
              <p className="text-lg font-bold text-orange-600">
                - Rs. {feeSummary.discount_amount?.toLocaleString()}
              </p>
            </div>
          )}
          
          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Paid Amount</p>
            <p className="text-lg font-bold text-green-600">
              Rs. {feeSummary.paid_amount?.toLocaleString()}
            </p>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Remaining</p>
            <p className={`text-lg font-bold ${feeSummary.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
              Rs. {feeSummary.remaining_amount?.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Discount Section (Admin Only) */}
      {user?.is_super_admin && (
        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
              <Percent className="w-4 h-4 text-orange-600" />
              Discount (Admin Only)
            </h4>
            {!feeSummary.discount ? (
              <Dialog open={showAddDiscount} onOpenChange={setShowAddDiscount}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50">
                    <Plus className="w-4 h-4 mr-1" /> Apply Discount
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Apply Fee Discount</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="text-xs bg-orange-100/60 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded p-2 text-orange-700 dark:text-orange-300">
                      Max discount: <strong>10%</strong> of the consultancy fee
                      {feeSummary?.total_fee
                        ? ` — up to Rs. ${Math.round((feeSummary.total_fee || 0) * 0.10).toLocaleString()} on a Rs. ${feeSummary.total_fee.toLocaleString()} fee.`
                        : '.'}
                    </div>
                    <div>
                      <Label>Discount Amount (PKR)</Label>
                      <Input
                        type="number"
                        max={feeSummary?.total_fee ? Math.round(feeSummary.total_fee * 0.10) : undefined}
                        value={newDiscount.discount_amount}
                        onChange={(e) => setNewDiscount({ ...newDiscount, discount_amount: e.target.value })}
                        placeholder="Enter amount"
                        data-testid="discount-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Reason (Required)</Label>
                      <Textarea
                        value={newDiscount.reason}
                        onChange={(e) => setNewDiscount({ ...newDiscount, reason: e.target.value })}
                        placeholder="Enter reason for discount..."
                        rows={3}
                      />
                    </div>
                    <Button onClick={handleApplyDiscount} disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Apply Discount
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={handleRemoveDiscount}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
          
          {feeSummary.discount && (
            <div className="bg-white dark:bg-slate-800 rounded-lg p-3 mt-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-orange-600">Rs. {feeSummary.discount.discount_amount?.toLocaleString()}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{feeSummary.discount.reason}</p>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <p>Approved by: {feeSummary.discount.approved_by_name}</p>
                  <p>{new Date(feeSummary.discount.approved_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Records */}
      <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
            <Receipt className="w-4 h-4 text-blue-600" />
            Payment Records ({feeSummary.payments?.length || 0})
          </h4>
          <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Payment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record Payment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Amount (PKR) *</Label>
                  <Input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                    placeholder="Enter amount"
                  />
                  {parseFloat(newPayment.amount) >= 25000 && (
                    <p className="text-xs text-teal-600 mt-1">💰 This payment will trigger Rs. 3,000 saving pot credit!</p>
                  )}
                </div>
                <div>
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment({ ...newPayment, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>
                          <div className="flex items-center gap-2">
                            <method.icon className="w-4 h-4" />
                            {method.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Received By *</Label>
                  <Select value={newPayment.received_by} onValueChange={(v) => setNewPayment({ ...newPayment, received_by: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={2}
                  />
                </div>
                <Button onClick={handleAddPayment} disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="submit-payment-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Record Payment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {feeSummary.payments?.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {feeSummary.payments.map((payment, idx) => {
              const MethodIcon = getPaymentMethodIcon(payment.payment_method);
              return (
                <div key={payment.id || idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <MethodIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">Rs. {payment.amount?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {payment.payment_method} • {new Date(payment.payment_date).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Received by: {payment.received_by_name}
                      </p>
                    </div>
                  </div>
                  {user?.is_super_admin && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                      onClick={() => handleDeletePayment(payment.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No payments recorded yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
